import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, mergeStyles } from '@fluentui/react';
import { Login } from './Pages/Login';
import { Portfolios } from './Pages/Portfolios';
import { Communities } from './Pages/Communities';
import { Tenants } from './Pages/Tenants';
import { AppShell } from './Components/AppShell';
import styles from './App.module.css';

const THEME_STORAGE_KEY = 'siteManagerTheme';

// Microsoft Teams inspired themes
const lightTheme = createTheme({
  palette: {
    themePrimary: '#5b5fc7',
    themeLighterAlt: '#f6f6ff',
    themeLighter: '#e8e9ff',
    themeLight: '#d4d6ff',
    themeTertiary: '#a2a6ff',
    themeSecondary: '#6f73d9',
    themeDarkAlt: '#4c50b1',
    themeDark: '#3e418a',
    themeDarker: '#2f3166',
    neutralLighterAlt: '#f7f7fb',
    neutralLighter: '#f2f2f8',
    neutralLight: '#e2e3ef',
    neutralQuaternaryAlt: '#d0d1df',
    neutralQuaternary: '#c4c6d4',
    neutralTertiaryAlt: '#aeb0bf',
    neutralTertiary: '#8b8fa3',
    neutralSecondary: '#5e6275',
    neutralPrimaryAlt: '#3b3f52',
    neutralPrimary: '#1e2235',
    neutralDark: '#141828',
    black: '#0c0f1a',
    white: '#ffffff',
  },
  semanticColors: {
    bodyBackground: '#f5f6fb',
    bodyText: '#1e2235',
    bodyTextChecked: '#1e2235',
    bodySubtext: '#5e6275',
    bodyDivider: '#e2e3ef',
    disabledBackground: '#f2f2f8',
    disabledText: '#8b8fa3',
    focusBorder: '#5b5fc7',
    variantBorder: '#e2e3ef',
    variantBorderHovered: '#d0d1df',
    defaultStateBackground: '#ffffff',
  },
});

const darkTheme = createTheme({
  palette: {
    themePrimary: '#8b90ff',
    themeLighterAlt: '#151627',
    themeLighter: '#1f2140',
    themeLight: '#2d2f61',
    themeTertiary: '#4c509a',
    themeSecondary: '#7076d9',
    themeDarkAlt: '#9a9eff',
    themeDark: '#b0b3ff',
    themeDarker: '#c9ccff',
    neutralLighterAlt: '#1b1d24',
    neutralLighter: '#20222a',
    neutralLight: '#2a2d36',
    neutralQuaternaryAlt: '#323643',
    neutralQuaternary: '#3a3f4b',
    neutralTertiaryAlt: '#5b6172',
    neutralTertiary: '#8a90a4',
    neutralSecondary: '#b3b8c8',
    neutralPrimaryAlt: '#d7dbea',
    neutralPrimary: '#f5f6ff',
    neutralDark: '#ffffff',
    black: '#ffffff',
    white: '#11131a',
  },
  semanticColors: {
    bodyBackground: '#11131a',
    bodyText: '#f5f6ff',
    bodyTextChecked: '#f5f6ff',
    bodySubtext: '#b3b8c8',
    bodyDivider: '#2a2d36',
    disabledBackground: '#1b1d24',
    disabledText: '#8a90a4',
    focusBorder: '#8b90ff',
    variantBorder: '#2a2d36',
    variantBorderHovered: '#3a3f4b',
    defaultStateBackground: '#1b1d24',
  },
});

// Global styles
mergeStyles({
  ':global(body,html,#root)': {
    margin: 0,
    padding: 0,
    height: '100vh',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  },
});

export const App: React.FC = () => {
  const [authenticated, setAuthenticated] = React.useState(false);
  const [checkingSession, setCheckingSession] = React.useState(true);
  const [themeMode, setThemeMode] = React.useState<'light' | 'dark'>(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'dark' ? 'dark' : 'light';
    } catch (error) {
      return 'light';
    }
  });

  const theme = themeMode === 'dark' ? darkTheme : lightTheme;

  React.useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch (error) {
      // Ignore storage errors
    }
    document.body.dataset.theme = themeMode;
    document.body.style.backgroundColor = theme.semanticColors.bodyBackground || '#f5f6fb';
  }, [themeMode, theme]);

  React.useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem('adminAccessToken');
      setAuthenticated(Boolean(token));
      setCheckingSession(false);
    };

    checkSession();
  }, []);

  if (checkingSession) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <ThemeProvider theme={theme}>
        <Login onLoginSuccess={() => setAuthenticated(true)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <AppShell
          onLogout={() => setAuthenticated(false)}
          themeMode={themeMode}
          onToggleTheme={() =>
            setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'))
          }
        >
          <Routes>
            <Route path="/" element={<Navigate to="/portfolios" replace />} />
            <Route path="/portfolios" element={<Portfolios />} />
            <Route path="/portfolios/:portfolioId/communities" element={<Communities />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="*" element={<Navigate to="/portfolios" replace />} />
          </Routes>
        </AppShell>
      </Router>
    </ThemeProvider>
  );
};

export default App;
